import {
  FC,
  memo,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  lazy,
  Suspense,
} from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { match } from "ts-pattern";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Badge from "@mui/material/Badge";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Skeleton from "@mui/material/Skeleton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Switch from "@mui/material/Switch";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SaveIcon from "@mui/icons-material/Save";
import DownloadIcon from "@mui/icons-material/Download";
import ShareIcon from "@mui/icons-material/Share";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useLazyQuery } from "@apollo/client";
import { useDialog } from "@/hooks/useDialog";
import { CompaniesExploreList } from "./CompaniesExploreList";
import { ConfirmationDialog } from "@/components/Dialog/ConfirmationDialog";
import { SegmentDialog } from "@/components/Segment/SegmentDialog";
import { SaveAsWorkbookDialog } from "@/components/Segment/SaveAsWorkbookDialog";
import { ShareLinkDialog } from "../MarketSizing/ShareLinkDialog";
import { hasModule, hasRole } from "@/utils/rbac";
import { SegmentModificationDialogs } from "@/components/Segment/SegmentModificationDialogs";
import CreditProgressBar from "@/components/Credits/CreditsProgressBar";
import { useSnackbar } from "@/components/Snackbar";
import {
  deleteSegment,
  fetchSegments,
  getSegment,
  getShareLink,
  updateSegment,
} from "@/apis/bacchus/segment";
import { SEARCH_COMPANY_QUERY } from "@/apis/companySearch/searchCompany";
import { COMPANY_SEARCH_SLIM_QUERY } from "@/apis/companySearch/companyList";
import { ExportQuote, Segment } from "@/apis/bacchus/types";
import { getExportQuote } from "@/apis/bacchus/exportQuote";
import {
  isShareLinkActive,
  type CreateShareLinkBody,
} from "@/apis/bacchus/shareTypes";
import { useTaxonomyContext } from "@/contexts/Taxonomy/TaxonomyContext";
import {
  CompanyFilterProvider,
  useCompanyFiltersController,
  type CompanyFilters,
  FiltersSidebar,
  FilterPanel,
  FiltersModal,
  CompanyExportModal,
  modules,
  FILTER_TITLES,
  FILTER_CATEGORIES,
  buildCompanyFilterLookups,
  useUploadedAccountsSync,
  useAiSpendCategories,
  isModuleKey,
  toGeographyOptions,
  type FilterKey,
  type FilterChip,
  type GeographyOptions,
  type ItSpendOptions,
  type AiSpendOptions,
  type FilterLookups,
  type TargetOrganizationsOptions,
  type UploadedAccountsOptions,
  type KnownCompaniesOptions,
  type IntentOptions,
} from "@/lib/company-filter";
import { safeParseFilters } from "@/lib/company-filter/utils/safeParse";
import { getScoringFilterStatus } from "@/lib/company-filter/modules/scoringFilterStatus";
import { getModelScoreFilterStatus } from "@/lib/company-filter/modules/modelScoreFilterStatus";
import {
  deriveTierFlags,
  InstallAgeSetting,
} from "@/lib/company-filter/modules/companyConfig";
import { useFeatureFlag } from "@/hooks/useLaunchDarkly";
import { isUniverseCategoryVisible } from "@/lib/company-filter/universeCategoryGates";
import { useScoringMethodModal } from "@/hooks/useScoringMethodModal";
import { openAiScoring } from "@/utils/module-urls";
import {
  useExportDisabledMessage,
  useExportRequiresFilters,
  useExportVisibility,
} from "@/hooks/useExportVisibility";
import {
  useScoringStore,
  v2SourceId,
  MAX_ACTIVE_PROFILES,
} from "@/store/scoringStore";
import {
  modelIdFromChipId,
  hasCriteria as modelScoreEntryHasCriteria,
} from "@/lib/company-filter/modules/modelScore";
import { getLoggedUserProfile, getRbacClaims, getTenantId } from "@/utils/auth";
import { useLocalStorage } from "@/utils/useLocalStorage";
import type { ScoringProfile, ScoringType } from "@/lib/scoring/types";
import { ExportChoiceDialog } from "./ExportChoiceDialog";
import type {
  MarketSizingContentHandle,
  MarketSizingShareConfig,
} from "../MarketSizing/MarketSizingContent";
import {
  hasShareConfigDrifted,
  toShareConfigBody,
} from "../MarketSizing/shareConfig";
import { useSegmentShareLink } from "../MarketSizing/useSegmentShareLink";
import { SaveSharedSegmentDialog } from "../components/SaveSharedSegmentDialog";
import { ShareNotOwnerDialog } from "../components/ShareNotOwnerDialog";

const ScoringProfileDialog = lazy(() =>
  import("@/lib/scoring/components/ScoringProfileDialog").then((m) => ({
    default: m.ScoringProfileDialog,
  }))
);
const ScoringWizardDialog = lazy(() =>
  import("@/lib/scoring/components/ScoringWizardDialog").then((m) => ({
    default: m.ScoringWizardDialog,
  }))
);
const MarketSizingContent = lazy(
  () => import("../MarketSizing/MarketSizingContent")
);

// The Companies page's content tab, mirrored into the `tab` URL param so a
// copied link lands on the same view. Absent param = the Company List tab
// (the page's primary function — Market Sizing is opt-in).
const URL_TAB_PARAM = "tab";
const MARKET_SIZING_TAB = "market-sizing";

import { LightTooltip } from "@/components/LightTooltip";
import { SegmentBreadcrumb } from "../components/SegmentBreadcrumb";
import { UnsavedSegmentChangesDialog } from "../components/UnsavedSegmentChangesDialog";
import { useSegmentNavigationBlocker } from "../hooks/useSegmentNavigationBlocker";
import { addRecentSegment } from "@/utils/recentSegments";
import { setAnalyticsSegmentName } from "@/utils/analyticsContext";
import {
  COMPANY_SEARCH_CLIENT,
  COMPANY_SEARCH_UPLOAD_CLIENT,
  COMPANY_MATCH_CLIENT,
  TAXONOMY_CLIENT,
} from "@/utils/consts";
import { useCommandPaletteStore } from "@/store/commandPalette/commandPaletteStore";
import {
  useUploadLazyQuery,
  useStatsLazyQuery,
} from "@/generated/company-match";
import { downloadMatchDetails } from "@/apis/companyMatch/restApi";
import { AxiosError } from "axios";
import {
  useGetIntentBuyersJourneyQuery,
  useGetIntentContextTypesQuery,
} from "@/generated/taxonomy";
import type { CompanySearchResponse } from "@/types/companySearch";
import type { SpogCompany } from "@/legacy/types/SpogCompany";
import { CompaniesExploreSearchInput } from "./components/CompaniesExploreSearchInput";
import { SegmentFiltersControl } from "./components/SegmentFiltersControl";
import {
  ScoringExploreTopbar,
  type ExploreViewMode,
} from "./components/ScoringExploreTopbar";
import { ScoringMatrix } from "./matrix/ScoringMatrix";
import { ScoringCompareMatrix } from "./matrix/ScoringCompareMatrix";
import {
  bandRange,
  BAND_LABEL,
  DEFAULT_THRESHOLDS,
  type Band,
  type MatrixThresholds,
} from "@/lib/scoring/matrix/bands";
import { profileColor } from "@/lib/scoring/profileColors";
import {
  useGetCompanyListBasicQuery,
  useUploadKnownCompaniesMutation,
  useKnownCompaniesUploadLazyQuery,
  KnownCompaniesUploadStatus,
  SortDir,
} from "@/generated/company-search";
import { transformFilterScoring } from "@/lib/scoring/scoringUtils";
import { useMarketSizingQueryArgs } from "../MarketSizing/useMarketSizingQueryArgs";
import {
  carrySegmentScopedSettings,
  EXPLORE_SCOPE,
} from "../MarketSizing/constants";

const LOCALIZE_INSTALLS_STORAGE_KEY = "localizeInstalls";
// Max companies a segment may have to be saved as a Sales Copilot workbook.
// Enforced here for UX and again in emback (createFromSegment) as the hard gate.
const MAX_WORKBOOK_COMPANIES = 50_000;
const VIEW_MODE_STORAGE_KEY = "segments.viewMode";

// ── Shareable scoring URL state ────────────────────────────────────────────
// The active scoring selection is mirrored into the `scoring` URL param
// (comma-joined source ids) so a copied link reproduces it. Scoring has no
// other persistence, unlike filters (segment metadata) and search.
const URL_SCORING_PARAM = "scoring";

/** Parses the `scoring` URL param into namespaced source ids. */
function parseScoringParam(raw: string | null): string[] {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Stamps the CURRENT active scoring selection into the serialized filters so the
 * segment captures which profile/model its scoring filters were built against
 * (RGI-2264). Re-stamped on every save from the live selection — the filter
 * store itself never round-trips this key (module-owned keys only).
 */
function withScoringSourceIds(
  filtersJson: string,
  sourceIds: string[]
): string {
  const parsed = safeParseFilters(filtersJson);
  if (!parsed && sourceIds.length === 0) return filtersJson;
  const next = { ...(parsed ?? {}) };
  if (sourceIds.length > 0) next.scoringSourceIds = sourceIds;
  else delete next.scoringSourceIds;
  return JSON.stringify(next);
}

// An export quote opened from the Quotes list (`?quote=<id>`): its filters,
// search and scoring are restored and the export modal opens with its
// settings. Kept in the URL after a save so Update targets the same quote.
const URL_QUOTE_PARAM = "quote";

/** Splits a quote's saved search filters into the page state they restore. */
function splitQuoteSearchFilters(quote: ExportQuote) {
  const {
    fuzzySearch,
    scoringSourceIds,
    ...filters
  }: ExportQuote["search_filters"] = quote.search_filters ?? {};
  return {
    filters: filters as CompanyFilters,
    search: fuzzySearch?.trim() ?? "",
    scoringSourceIds: scoringSourceIds ?? [],
  };
}

/** Order-insensitive equality of two scoring-source selections. */
function sameSourceSelection(a: string[], b: string[]): boolean {
  return (
    a.length === b.length && [...a].sort().join(",") === [...b].sort().join(",")
  );
}

/** Sets a query param, or removes it entirely when the value is empty. */
function setParamOrDelete(
  params: URLSearchParams,
  key: string,
  value: string
): void {
  if (value) params.set(key, value);
  else params.delete(key);
}

function formatRefreshed(iso?: string): string | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return "Refreshed today";
  if (days === 1) return "Refreshed yesterday";
  if (days < 30) return `Refreshed ${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12)
    return `Refreshed ${months} month${months > 1 ? "s" : ""} ago`;
  const years = Math.floor(months / 12);
  return `Refreshed ${years} year${years > 1 ? "s" : ""} ago`;
}

const SEGMENT_ERROR_MESSAGES = {
  NOT_FOUND: "Segment not found",
  NO_ACCESS: "You don't have access to this segment",
  GENERIC:
    "Unable to load segment. Please try again or contact support if the problem persists.",
} as const;

type SearchCompaniesV2Response = {
  searchCompaniesV2?: {
    data?: SpogCompany[];
    count?: number;
  };
};

function spogCompanyToCached(company: SpogCompany) {
  return {
    hgId: company.hgId,
    name: company.name,
    url: company.url ?? undefined,
  };
}

const LocalizedInstallsFooter: FC<{
  checked: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}> = memo(({ checked, onChange }) => {
  const tooltipTitle = (
    <Box sx={{ p: 0.5, maxWidth: 320 }}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.75 }}>
        Localized Installs
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.75 }}>
        Only return companies where install data is detected in the same country
        as the company (Recommended)
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", lineHeight: 1.5 }}
      >
        Disabling this option will return companies if an install is detected
        anywhere in the company's global footprint, even outside the country of
        the company. Applicable when filtering by technographic data.
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ width: "100%" }}>
      <InstallAgeSetting />
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2" fontWeight={600}>
          Localized Installs
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Switch
            checked={checked}
            onChange={onChange}
            disableRipple
            size="small"
          />
          <LightTooltip title={tooltipTitle} placement="top">
            <IconButton
              className="localized-installs-info"
              size="small"
              sx={{
                color: "text.secondary",
                opacity: 0.6,
                transition: "opacity 150ms ease",
                "&:hover, &:focus-visible": { opacity: 1 },
              }}
              aria-label="Localized installs info"
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </LightTooltip>
        </Stack>
      </Stack>
    </Box>
  );
});

LocalizedInstallsFooter.displayName = "LocalizedInstallsFooter";

const CompaniesExplorePageContent: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { segmentId } = useParams<{ segmentId: string }>();
  const { showInfo, showSuccess, showError } = useSnackbar();
  const addRecentItem = useCommandPaletteStore((s) => s.addRecentItem);

  const urlSearch = searchParams.get("search") ?? "";
  const quoteId = searchParams.get(URL_QUOTE_PARAM);
  const [quote, setQuote] = useState<ExportQuote | null>(null);

  // Market Sizing tab is behind a flag while it's shared out for review —
  // default false hides it entirely, including a direct `?tab=market-sizing`
  // link (the param is ignored unless the flag is on).
  const marketSizingTabEnabled = useFeatureFlag("maMarketSizingTab", false);
  const activeTab =
    marketSizingTabEnabled &&
    searchParams.get(URL_TAB_PARAM) === MARKET_SIZING_TAB
      ? MARKET_SIZING_TAB
      : "list";
  // Market Sizing mounts on first visit and then stays mounted (hidden) for
  // the rest of the page's lifetime — same trade-off the Company List side
  // already makes in reverse. Unmounting it on every tab switch would drop
  // ScoringProfileTabs' per-profile tier selections (they live in that
  // component's local state, seeded from the shared filter only at mount).
  const [marketSizingVisited, setMarketSizingVisited] = useState(false);
  useEffect(() => {
    if (activeTab === MARKET_SIZING_TAB) setMarketSizingVisited(true);
  }, [activeTab]);

  // Sync hooks for filters that need async data from server
  useUploadedAccountsSync();

  // Feature flags for filter visibility
  const intentFiltersEnabled = useFeatureFlag(
    "marketAnalyzerIntentFiltersEnabled",
    false
  );
  const scoringEnabled = useFeatureFlag("marketAnalyzerScoringEnabled", false);
  const universeJsonExportEnabled = useFeatureFlag("universeJsonExport", false);
  const scoringV2Enabled = useFeatureFlag("maScoringModelsV2", false);
  const showMethodModal = useScoringMethodModal();

  // Scoring store state for dynamic filter enablement
  const activeProfileIds = useScoringStore((s) => s.activeProfileIds);
  const activeModelFamilies = useScoringStore((s) => s.activeModelFamilies);
  const models = useScoringStore((s) => s.models);
  const isModelsLoading = useScoringStore((s) => s.isModelsLoading);
  const activeSourceIds = useScoringStore((s) => s.activeSourceIds);
  const setActiveSourceIds = useScoringStore((s) => s.setActiveSourceIds);
  const activateSource = useScoringStore((s) => s.activateSource);
  const deactivateSource = useScoringStore((s) => s.deactivateSource);
  const profiles = useScoringStore((s) => s.profiles);
  const focusedProfileId = useScoringStore((s) => s.focusedProfileId);
  const compareProfileId = useScoringStore((s) => s.compareProfileId);
  const setCompareProfileId = useScoringStore((s) => s.setCompareProfileId);

  // Filter categories based on feature flags and scoring state
  const filteredCategories = useMemo(
    () =>
      FILTER_CATEGORIES.filter((cat) => {
        if (cat.id === "intent" && !intentFiltersEnabled) return false;
        if (cat.id === "fitScore" || cat.id === "intentScore") {
          if (!scoringEnabled) return false;
          // When advanced (V2) scoring is available and no V1 profile is active,
          // the user scores with models — hide the V1 profile score filters and
          // surface only the model-score filter instead.
          if (scoringV2Enabled && activeProfileIds.length === 0) return false;
        }
        if (cat.id === "modelScore" && !scoringV2Enabled) return false;
        return isUniverseCategoryVisible(cat.id, universeJsonExportEnabled);
      }).map((cat) => {
        if (cat.id === "modelScore") {
          const status = getModelScoreFilterStatus(
            models.length,
            isModelsLoading
          );
          return {
            ...cat,
            enabled: status.enabled,
            disabledReason: status.disabledReason ?? undefined,
          };
        }
        if (cat.id !== "fitScore" && cat.id !== "intentScore") return cat;
        const status = getScoringFilterStatus(
          cat.id === "fitScore" ? "fit" : "intent",
          activeProfileIds,
          profiles
        );
        return {
          ...cat,
          enabled: status.enabled,
          disabledReason: status.disabledReason ?? undefined,
        };
      }),
    [
      intentFiltersEnabled,
      scoringEnabled,
      universeJsonExportEnabled,
      scoringV2Enabled,
      activeProfileIds,
      models.length,
      isModelsLoading,
      profiles,
    ]
  );

  const isCreateMode = searchParams.get("action") === "new";
  const isEditMode = !!segmentId;
  const isExploreMode = !isCreateMode && !isEditMode;
  // Identity of the current view — changes only when we navigate between
  // segments/modes, not when query params (search, our own sync) change. Used
  // to re-arm the one-shot URL hydration below.
  const navKey = isEditMode
    ? `edit:${segmentId}`
    : isCreateMode
      ? "create"
      : "explore";

  const saveDialog = useDialog();
  const editDialog = useDialog<Segment>();
  const deleteDialog = useDialog();
  const exportDialog = useDialog();
  const exportChoiceDialog = useDialog();
  const saveAsWorkbookDialog = useDialog();
  const shareDialog = useDialog<CreateShareLinkBody["config"]>();
  const sharedSegmentDialog = useDialog();
  const shareNotOwnerDialog = useDialog();
  const [isSavingShared, setIsSavingShared] = useState(false);
  const [liveShareConfig, setLiveShareConfig] =
    useState<MarketSizingShareConfig | null>(null);
  const marketSizingRef = useRef<MarketSizingContentHandle>(null);
  // Mutating a ref's .current never re-renders the reader, so the Share
  // button's disabled check needs this state instead — set by a callback ref, which fires exactly when the lazy tab mounts/unmounts.
  const [marketSizingReady, setMarketSizingReady] = useState(false);
  const setMarketSizingRef = useCallback(
    (instance: MarketSizingContentHandle | null) => {
      marketSizingRef.current = instance;
      setMarketSizingReady(instance !== null);
    },
    []
  );
  // Polled by the export between steps — a ref, not state, so the running
  // export sees the flip without waiting for a re-render.
  const exportCancelledRef = useRef(false);
  const [marketSizingExporting, setMarketSizingExporting] = useState(false);

  // Sales Copilot access gates whether the "Add to Sales Copilot" button is
  // enabled (disabled with a tooltip otherwise). Lists exist only in AI Sales
  // Copilot, so RBAC v2 users need the `ai_sales_copilot` module (RGI-2253) —
  // not the legacy `sales_copilot` (MSI), which has no lists to open. v1 users
  // (no `mkClaims.rbac`) and SuperKudu keep access (hasModule short-circuits
  // to true for them).
  const { rbac, superKudu } = getRbacClaims();
  const canSaveAsWorkbook = hasModule(rbac, "ai_sales_copilot", {
    superKudu: !!superKudu,
  });
  const canLinkOffering = hasRole(rbac, "orgAdmin", {
    superKudu: !!superKudu,
  });

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);

  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);
  // Explore-mode URL sync: `hydratedRef` marks that we've read the URL into the
  // stores for the current explore session; `skipWriteRef` swallows the write
  // that fires on the same commit as a hydrate (store selectors still lag), so
  // hydration never clobbers the shared params with stale/empty values.
  const hydratedRef = useRef(false);
  const skipWriteRef = useRef(false);
  const [activeFilter, setActiveFilter] = useState<FilterKey | null>(null);
  const [localizeInstalls, setLocalizeInstalls] = useLocalStorage<boolean>(
    LOCALIZE_INSTALLS_STORAGE_KEY,
    true
  );
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  // Shown when the user tries to close the filters modal with unconfirmed edits.
  const [discardFiltersOpen, setDiscardFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useLocalStorage<ExploreViewMode>(
    VIEW_MODE_STORAGE_KEY,
    "list"
  );
  const [compareMode, setCompareMode] = useState(false);
  const [thresholds, setThresholds] =
    useState<MatrixThresholds>(DEFAULT_THRESHOLDS);
  // Ephemeral fit+intent band from a matrix cell drill-down (not persisted).
  const [drillBand, setDrillBand] = useState<{
    fit: Band;
    intent: Band;
  } | null>(null);
  const handleViewModeChange = useCallback(
    (mode: ExploreViewMode) => {
      setViewMode(mode);
      if (mode === "matrix") setDrillBand(null);
    },
    [setViewMode]
  );

  const handleMatrixCellClick = useCallback(
    (fit: Band, intent: Band) => {
      setDrillBand({ fit, intent });
      setViewMode("list");
    },
    [setViewMode]
  );

  const [segment, setSegment] = useState<Segment | null>(null);
  const [isLoadingSegment, setIsLoadingSegment] = useState(!!segmentId);
  const [companyCount, setCompanyCount] = useState<number>(0);
  const [allSegments, setAllSegments] = useState<Segment[]>([]);
  const [activeSortEntry, setActiveSortEntry] = useState<{
    field: string;
    direction: string;
  }>({ field: "revenue_range_min", direction: "desc" });
  // Stable callback + skip-equal-update guards against an infinite render
  // loop: CompaniesExploreList's effect that calls onActiveSortChange has
  // the callback in its deps, and setState with a fresh object literal would
  // re-render the page → re-create the inline arrow → effect refires.
  const handleActiveSortChange = useCallback(
    (field: string, direction: string) =>
      setActiveSortEntry((prev) =>
        prev.field === field && prev.direction === direction
          ? prev
          : { field, direction }
      ),
    []
  );
  const canExport = useExportVisibility();
  const exportRequiresFilters = useExportRequiresFilters();
  const exportDisabledMessage = useExportDisabledMessage();

  // Extract permissions from segment
  const canEdit = segment?.permissions?.write ?? false;
  const canDelete = segment?.permissions?.delete ?? false;

  const pageTitle = match({ isCreateMode, isEditMode })
    .with({ isCreateMode: true }, () => "New Segment")
    .with({ isEditMode: true }, () => segment?.name ?? "")
    .otherwise(() => "Companies");

  const breadcrumbMode = match({ isCreateMode, isEditMode })
    .with({ isCreateMode: true }, () => "create" as const)
    .with({ isEditMode: true }, () => "edit" as const)
    .otherwise(() => "browse" as const);

  // Determine save button configuration based on mode and permissions
  // Note: While loading in edit mode, we show "Save Segment" to avoid UI flicker
  const saveButtonConfig = match({
    isCreateMode,
    isExploreMode,
    isLoadingSegment,
    canEdit,
  })
    .with({ isCreateMode: true }, () => ({
      action: "create" as const,
      label: "Create Segment",
    }))
    .with({ isExploreMode: true }, () => ({
      action: "save" as const,
      label: "Save Segment",
    }))
    .with({ isLoadingSegment: true }, () => ({
      action: "saveFilters" as const,
      label: "Save Segment",
    }))
    .with({ canEdit: true }, () => ({
      action: "saveFilters" as const,
      label: "Save Segment",
    }))
    .otherwise(() => ({
      action: "saveAsNew" as const,
      label: "Save as New",
    }));

  // Determine which actions are available in the dropdown menu
  // Note: showSaveAsNew is false when it's already the primary button action
  const menuActions = match({ canEdit, canDelete })
    .with({ canEdit: false, canDelete: false }, () => ({
      showSaveAsNew: false,
      showDivider: false,
      showEdit: false,
      showDelete: false,
    }))
    .with({ canEdit: true, canDelete: true }, () => ({
      showSaveAsNew: true,
      showDivider: true,
      showEdit: true,
      showDelete: true,
    }))
    .with({ canEdit: true }, () => ({
      showSaveAsNew: true,
      showDivider: true,
      showEdit: true,
      showDelete: false,
    }))
    .with({ canDelete: true }, () => ({
      showSaveAsNew: false,
      showDivider: true,
      showEdit: false,
      showDelete: true,
    }))
    .exhaustive();

  const {
    slices,
    setSlice,
    appliedFilters,
    hasUnsavedChanges,
    hasPendingChanges,
    chips: filterChips,
    getFilterCount,
    applyDraft,
    revertDraftToApplied,
    hydrateFromCompanyFilters,
    serializeAppliedToJson,
    markAsPersisted,
    clearDraft,
    clearAll,
  } = useCompanyFiltersController({ autoApply: false });

  // Enrich score filter chips with tooltip when filter is disabled but has values
  const enrichedChips = useMemo(() => {
    const fitCat = filteredCategories.find((c) => c.id === "fitScore");
    const intentCat = filteredCategories.find((c) => c.id === "intentScore");
    return filterChips.map((chip) => {
      if (
        chip.filterKey === "fitScore" &&
        fitCat &&
        !fitCat.enabled &&
        fitCat.disabledReason
      ) {
        return {
          ...chip,
          tooltipContent: fitCat.disabledReason,
          disabled: true,
        };
      }
      if (
        chip.filterKey === "intentScore" &&
        intentCat &&
        !intentCat.enabled &&
        intentCat.disabledReason
      ) {
        return {
          ...chip,
          tooltipContent: intentCat.disabledReason,
          disabled: true,
        };
      }
      // A model-score filter keeps applying even when its model is removed
      // from the view (RGI-2347) — only removing the chip unfilters. Gray the
      // chip only when the model is gone from the catalog entirely (deleted/
      // unpublished), because then the filter can no longer resolve.
      if (
        chip.filterKey === "modelScore" &&
        scoringV2Enabled &&
        !isModelsLoading &&
        !models.some((m) => m.family === modelIdFromChipId(chip.id))
      ) {
        return {
          ...chip,
          tooltipContent:
            "This model no longer exists in your workspace, so the filter is inactive. Remove the chip to clear it.",
          disabled: true,
        };
      }
      return chip;
    });
  }, [
    filterChips,
    filteredCategories,
    scoringV2Enabled,
    isModelsLoading,
    models,
  ]);

  // Market Sizing names the filters its figures were computed from, in the
  // toolbar chips' words (their summary labels list products and vendors by
  // name). The chips follow the draft, so they are read only while it matches
  // the applied filters and kept as they were while an edit is unconfirmed.
  // A disabled chip narrows nothing. Model scores are listed only while no
  // model is active: with a scoring tab open, only its model narrows the
  // figures, and the "Graded by" line and the tabs already say which.
  const [appliedFilterChips, setAppliedFilterChips] = useState(enrichedChips);
  useEffect(() => {
    if (!hasPendingChanges) setAppliedFilterChips(enrichedChips);
  }, [hasPendingChanges, enrichedChips]);
  const marketSizingFilterLabels = useMemo(() => {
    const labels = appliedFilterChips
      .filter(
        (chip) =>
          !chip.disabled &&
          (chip.filterKey !== "modelScore" || activeModelFamilies.length === 0)
      )
      .map((chip) => chip.summaryLabel ?? chip.label);
    const search = urlSearch.trim();
    return search ? [...labels, `Search: “${search}”`] : labels;
  }, [appliedFilterChips, activeModelFamilies, urlSearch]);

  // The scoring selection lives in a separate store, so filter-store dirtiness
  // (hasUnsavedChanges) misses it. Track the selection the segment was last
  // saved/hydrated with and treat a divergence as an unsaved change, so changing
  // the scoring profile alone still lets the user save (RGI-2264).
  const [persistedSourceIds, setPersistedSourceIds] = useState<string[]>([]);
  const scoringChanged =
    isEditMode && !sameSourceSelection(activeSourceIds, persistedSourceIds);

  // Navigation blocking for unsaved changes
  const { isBlocked, proceed, reset } = useSegmentNavigationBlocker(
    isSavingRef,
    scoringChanged
  );

  // Browser close/refresh protection
  useEffect(() => {
    const hasChangesToProtect = isCreateMode
      ? appliedFilters !== undefined && Object.keys(appliedFilters).length > 0
      : hasUnsavedChanges || scoringChanged;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasChangesToProtect) {
        event.preventDefault();
        event.returnValue = "";
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isCreateMode, appliedFilters, hasUnsavedChanges, scoringChanged]);

  const handleDebouncedSearchChange = useCallback(
    (nextValue: string) => {
      const nextSearch = nextValue.trim();

      if (nextSearch === urlSearch) return;

      const nextParams = new URLSearchParams(searchParams);
      if (!nextSearch) {
        nextParams.delete("search");
      } else {
        nextParams.set("search", nextSearch);
      }

      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams, urlSearch]
  );

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, value: string) => {
      const nextParams = new URLSearchParams(searchParams);
      setParamOrDelete(
        nextParams,
        URL_TAB_PARAM,
        value === MARKET_SIZING_TAB ? value : ""
      );
      // Push (don't replace) so the browser Back button returns to the
      // previous tab rather than leaving the page.
      setSearchParams(nextParams);
    },
    [searchParams, setSearchParams]
  );

  // Switches tabs without pushing a history entry — used by the export flow
  // below to hop over to Market Sizing and back as a side effect of one
  // click, not a real navigation the user would expect Back to undo (that's
  // what handleTabChange, a genuine tab click, is for).
  const setActiveTabParam = useCallback(
    (tab: string) => {
      const nextParams = new URLSearchParams(searchParams);
      setParamOrDelete(
        nextParams,
        URL_TAB_PARAM,
        tab === MARKET_SIZING_TAB ? tab : ""
      );
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // "Export" → "Market Sizing" (see ExportChoiceDialog) needs the tab's own
  // live query state (isActive gates every query it fires — see
  // MarketSizingContent), so unlike Company List's export (a plain filter
  // snapshot passed to a modal) this has to actually switch to the tab,
  // wait for it to mount (lazy) and settle, then call back into it via ref
  // — see MarketSizingContentHandle. Restores whichever tab was open before,
  // since this is a background side effect of clicking Export, not a
  // navigation the user asked for.
  const handleExportMarketSizing = useCallback(async () => {
    const originalTab = activeTab;
    const needsTabSwitch = originalTab !== MARKET_SIZING_TAB;
    exportCancelledRef.current = false;
    setMarketSizingExporting(true);
    const isCancelled = () => exportCancelledRef.current;
    try {
      setMarketSizingVisited(true);
      if (needsTabSwitch) setActiveTabParam(MARKET_SIZING_TAB);

      // Generous: opening the tab cold fires the headline, universe and
      // per-tier queries at once, and with several scoring models active that
      // first load runs well past 15s — which used to abandon the export with
      // a "not ready" error while the page was still perfectly fine.
      const deadline = Date.now() + 60000;
      while (
        Date.now() < deadline &&
        !isCancelled() &&
        !marketSizingRef.current?.canExport
      ) {
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
      if (isCancelled()) {
        exportChoiceDialog.close();
        return;
      }
      if (!marketSizingRef.current?.canExport) {
        showError(
          "Market Sizing isn't ready to export yet — try again in a moment."
        );
        return;
      }
      await marketSizingRef.current.exportHtml(isCancelled);
      exportChoiceDialog.close();
    } catch (err) {
      console.error("Exporting Market Sizing failed:", err);
      showError(
        "Couldn't export Market Sizing — reload the page and try again."
      );
    } finally {
      if (needsTabSwitch) setActiveTabParam(originalTab);
      setMarketSizingExporting(false);
    }
  }, [activeTab, setActiveTabParam, showError, exportChoiceDialog]);

  // The Market Sizing tab's "add a scoring profile" hint jumps to the same
  // picker rendered in the toolbar below (ScoringSourcePills/ScoringProfilePills
  // manage their own anchor/menu state internally with no external open prop),
  // so this simulates a click on its rendered "add" button rather than
  // duplicating the picker UI.
  const scoringPickerRef = useRef<HTMLDivElement>(null);
  const handleOpenScoringPicker = useCallback(() => {
    scoringPickerRef.current
      ?.querySelector<HTMLElement>(
        '[data-testid="scoring-add-source-btn"], [data-testid="scoring-add-profile-btn"]'
      )
      ?.click();
  }, []);

  // Reusable function to load all segments
  const loadAllSegments = useCallback(async () => {
    try {
      const response = await fetchSegments({
        filterBy: "all",
        pageSize: 500,
      });
      setAllSegments(response.data ?? []);
    } catch (error) {
      console.error("Failed to load segments:", error);
    }
  }, []);

  // Preload all segments on page mount
  useEffect(() => {
    loadAllSegments();
  }, [loadAllSegments]);

  // Fetch scoring profiles when the scoring feature flag is enabled
  const fetchProfiles = useScoringStore((s) => s.fetchProfiles);
  useEffect(() => {
    if (!scoringEnabled) return;
    const email = getLoggedUserProfile()?.email;
    if (email) void fetchProfiles(email);
  }, [scoringEnabled, fetchProfiles]);

  // Fetch the V2 model catalog here too (not only in CompaniesExploreList):
  // the model-score filter panel, its chips (names) and the sidebar gating all
  // need it even when the list isn't mounted (e.g. matrix view).
  const fetchModels = useScoringStore((s) => s.fetchModels);
  useEffect(() => {
    if (!scoringV2Enabled) return;
    const tenantId = getTenantId();
    if (tenantId) void fetchModels(tenantId);
  }, [scoringV2Enabled, fetchModels]);

  // Scoring profile dialog state
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<ScoringProfile | null>(
    null
  );
  const [wizardOpen, setWizardOpen] = useState(false);
  const [generatedCriteria, setGeneratedCriteria] = useState<
    ScoringType[] | undefined
  >(undefined);
  const [generatedName, setGeneratedName] = useState<string | undefined>(
    undefined
  );

  // Refetch segments when segmentId changes (user switches segments)
  useEffect(() => {
    if (segmentId) {
      loadAllSegments();
    }
  }, [segmentId, loadAllSegments]);

  // Show info snackbar when entering create mode
  useEffect(() => {
    if (isCreateMode) {
      showInfo(
        "Configure your segment criteria, then click 'Create Segment' when ready."
      );
    }
  }, [isCreateMode, showInfo]);

  // Load segment data when in edit mode, clear when leaving
  useEffect(() => {
    if (segmentId) {
      setIsLoadingSegment(true);
      getSegment(segmentId)
        .then((seg) => {
          setSegment(seg);
          setAnalyticsSegmentName(seg.name);
          // Track recent segment access
          if (seg.id && seg.name) {
            addRecentSegment({ id: seg.id, name: seg.name });
            addRecentItem({
              id: seg.id,
              type: "segment",
              name: seg.name,
              route: `/segments/${seg.id}`,
            });
          }
        })
        .catch((err) => {
          if (err instanceof AxiosError) {
            const status = err.response?.status;
            if (status === 404) {
              showError(SEGMENT_ERROR_MESSAGES.NOT_FOUND);
            } else if (status === 403) {
              showError(SEGMENT_ERROR_MESSAGES.NO_ACCESS);
            } else {
              showError(SEGMENT_ERROR_MESSAGES.GENERIC);
            }
          } else {
            showError(SEGMENT_ERROR_MESSAGES.GENERIC);
          }
          navigate("/segments");
        })
        .finally(() => setIsLoadingSegment(false));
    } else {
      // Clear segment state when navigating away from edit mode
      setSegment(null);
      setAnalyticsSegmentName(undefined);
    }
    return () => setAnalyticsSegmentName(undefined);
  }, [segmentId, showError, navigate, addRecentItem]);

  // Load the quote named in the URL; dropping the param drops the quote.
  useEffect(() => {
    if (!quoteId) {
      setQuote(null);
      return;
    }
    if (quote?.id === quoteId) return;
    let cancelled = false;
    getExportQuote(quoteId)
      .then((loaded) => {
        if (!cancelled) setQuote(loaded);
      })
      .catch(() => {
        if (cancelled) return;
        showError("Unable to load this quote.");
        navigate("/quotes");
      });
    return () => {
      cancelled = true;
    };
  }, [quoteId, quote?.id, showError, navigate]);

  const handleQuoteSaved = useCallback(
    (saved: ExportQuote) => {
      setQuote(saved);
      if (saved.id === quoteId) return;
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set(URL_QUOTE_PARAM, saved.id);
      setSearchParams(nextParams, { replace: true });
    },
    [quoteId, searchParams, setSearchParams]
  );

  // Re-arm the one-shot hydration whenever we navigate to a different
  // segment/mode (but not when only query params change).
  useEffect(() => {
    hydratedRef.current = false;
  }, [navKey]);

  // Hydrate the applied state once per navigation. Filters come from the
  // segment metadata in edit mode (nothing in explore/create); the active
  // scoring selection comes from the URL so a copied link reproduces it.
  // Guarded to run once so later query changes (search typing, our own scoring
  // sync writes) don't wipe in-progress edits.
  useEffect(() => {
    if (hydratedRef.current) return;
    if (isEditMode) {
      // Wait until the segment matching the current route has loaded.
      if (String(segment?.id) !== segmentId) return;
      const parsedFilters = segment?.metadata?.filters
        ? safeParseFilters(segment.metadata.filters)
        : undefined;
      hydrateFromCompanyFilters(parsedFilters);
      // The URL still wins when present (a copied link reproduces its exact
      // scoring selection); otherwise restore the selection the segment was
      // saved with, so its scoring filters apply against the same profile/model
      // instead of silently deactivating (RGI-2264).
      const rawScoringParam = searchParams.get(URL_SCORING_PARAM);
      setActiveSourceIds(
        rawScoringParam !== null
          ? parseScoringParam(rawScoringParam)
          : (parsedFilters?.scoringSourceIds ?? [])
      );
      // Baseline is what the segment was SAVED with (not the URL override), so a
      // copied link whose scoring differs correctly reads as an unsaved change.
      setPersistedSourceIds(parsedFilters?.scoringSourceIds ?? []);
    } else if (isExploreMode && quoteId) {
      // Wait until the quote matching the URL has loaded.
      if (quote?.id !== quoteId) return;
      const restored = splitQuoteSearchFilters(quote);
      hydrateFromCompanyFilters(restored.filters);
      setActiveSourceIds(restored.scoringSourceIds);
      const nextParams = new URLSearchParams(searchParams);
      setParamOrDelete(nextParams, "search", restored.search);
      setSearchParams(nextParams, { replace: true });
      exportDialog.open();
    } else if (isExploreMode) {
      hydrateFromCompanyFilters(undefined);
      setActiveSourceIds(
        parseScoringParam(searchParams.get(URL_SCORING_PARAM))
      );
    } else {
      // Create mode (?action=new): start from a clean slate.
      clearAll();
    }
    hydratedRef.current = true;
    skipWriteRef.current = true;
  }, [
    isEditMode,
    isExploreMode,
    segment,
    segmentId,
    quote,
    quoteId,
    searchParams,
    setSearchParams,
    exportDialog,
    hydrateFromCompanyFilters,
    setActiveSourceIds,
    clearAll,
  ]);

  // Mirror the active scoring selection into the `scoring` URL param so a copied
  // link reproduces it (explore + edit modes; never in create mode).
  useEffect(() => {
    if (!hydratedRef.current || (!isExploreMode && !isEditMode)) return;
    if (skipWriteRef.current) {
      // Swallow the write triggered by the hydrate commit (see refs above).
      skipWriteRef.current = false;
      return;
    }
    const next = new URLSearchParams(searchParams);
    setParamOrDelete(next, URL_SCORING_PARAM, activeSourceIds.join(","));
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [
    isExploreMode,
    isEditMode,
    activeSourceIds,
    searchParams,
    setSearchParams,
  ]);

  const handleSaveClick = useCallback(() => {
    saveDialog.open();
  }, [saveDialog]);

  const saveFilters = useCallback(async () => {
    if (!segmentId) return;
    try {
      await updateSegment(segmentId, {
        metadata: {
          filters: withScoringSourceIds(
            serializeAppliedToJson(),
            activeSourceIds
          ),
        },
      });
      showSuccess("Segment saved successfully");
      markAsPersisted();
      setPersistedSourceIds(activeSourceIds);
    } catch {
      showError("Failed to save segment");
    }
  }, [
    segmentId,
    serializeAppliedToJson,
    activeSourceIds,
    showSuccess,
    showError,
    markAsPersisted,
  ]);

  // Only the creator can read the share link; the segment page itself is
  // otherwise unaffected by it.
  const canManageShare =
    marketSizingTabEnabled &&
    isEditMode &&
    Boolean(segment?.permissions?.write);
  const { link: shareLink, setLink: setShareLink } = useSegmentShareLink(
    segmentId,
    canManageShare
  );

  // A saved segment's filters are read live by its share link, so saving
  // silently changes what prospects see: ask first. The link is re-read here
  // because it may have been created or expired since the page loaded.
  const handleSaveFilters = useCallback(async () => {
    if (!segmentId || !canManageShare) return saveFilters();
    try {
      const current = await getShareLink(segmentId);
      setShareLink(current);
      if (isShareLinkActive(current)) {
        sharedSegmentDialog.open();
        return;
      }
    } catch {
      showError("Couldn't check whether this segment is shared. Try again.");
      return;
    }
    await saveFilters();
  }, [
    segmentId,
    canManageShare,
    saveFilters,
    setShareLink,
    sharedSegmentDialog,
    showError,
  ]);

  const handleSaveAndUpdateSharedLink = useCallback(async () => {
    setIsSavingShared(true);
    try {
      await saveFilters();
    } finally {
      setIsSavingShared(false);
      sharedSegmentDialog.close();
    }
  }, [saveFilters, sharedSegmentDialog]);

  const handleSaveSharedAsNew = useCallback(() => {
    sharedSegmentDialog.close();
    saveDialog.open();
  }, [sharedSegmentDialog, saveDialog]);

  const handleSaveComplete = useCallback(
    (newSegmentId?: string) => {
      saveDialog.close();
      if (newSegmentId) {
        // Before navigating: Market Sizing's tier names, hidden tiers and
        // spend categories are keyed by segment id, so the new segment starts
        // with none of what the user just set up — and Share, which freezes
        // exactly those, only appears once a segment is saved.
        carrySegmentScopedSettings(segmentId ?? EXPLORE_SCOPE, newSegmentId);
        // Mark as saving to prevent navigation blocker from triggering
        // Use ref for synchronous update (state updates are async)
        isSavingRef.current = true;
        // The open tab goes with it. Which tab you are on lives only in the
        // URL, so navigating to a bare path drops it and drops the user back
        // on Company List — saving from Market Sizing then reads as the save
        // having navigated away from the view they were working in. Only the
        // tab is carried: everything else about the new segment comes from
        // what was just saved, not from the old segment's query string.
        const nextParams = new URLSearchParams();
        setParamOrDelete(
          nextParams,
          URL_TAB_PARAM,
          activeTab === MARKET_SIZING_TAB ? MARKET_SIZING_TAB : ""
        );
        const search = nextParams.toString();
        // Navigate to the edit mode with the new segment ID
        navigate(`/segments/${newSegmentId}${search ? `?${search}` : ""}`);
      }
    },
    [saveDialog, navigate, segmentId, activeTab]
  );

  // Menu handlers (edit mode)
  const handleMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setMenuAnchorEl(event.currentTarget);
    },
    []
  );

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  const handleSaveAsNewClick = useCallback(() => {
    handleMenuClose();
    saveDialog.open();
  }, [handleMenuClose, saveDialog]);

  // Map save actions to their respective handlers (single source of truth)
  const saveActionHandlers = useMemo(
    () => ({
      create: handleSaveClick,
      save: handleSaveClick,
      saveFilters: handleSaveFilters,
      saveAsNew: handleSaveAsNewClick,
    }),
    [handleSaveClick, handleSaveFilters, handleSaveAsNewClick]
  );

  const handleEditClick = useCallback(() => {
    handleMenuClose();
    if (segment) {
      editDialog.open(segment);
    }
  }, [handleMenuClose, segment, editDialog]);

  const handleDeleteClick = useCallback(() => {
    handleMenuClose();
    deleteDialog.open();
  }, [handleMenuClose, deleteDialog]);

  const handleConfirmDelete = useCallback(async () => {
    if (!segmentId) return;
    setIsDeleting(true);
    try {
      await deleteSegment(segmentId);
      showSuccess("Segment deleted successfully");
      navigate("/segments");
    } catch {
      showError("Failed to delete segment");
    } finally {
      setIsDeleting(false);
      deleteDialog.close();
    }
  }, [segmentId, showSuccess, showError, navigate, deleteDialog]);

  const handleEditComplete = useCallback(() => {
    editDialog.close();
    // Reload segment data and refresh dropdown list
    if (segmentId) {
      setIsLoadingSegment(true);
      getSegment(segmentId)
        .then(setSegment)
        .catch(console.error)
        .finally(() => setIsLoadingSegment(false));
      loadAllSegments();
    }
  }, [segmentId, editDialog, loadAllSegments]);

  // Navigation blocker dialog handlers
  const handleBlockerSave = useCallback(async () => {
    if (isCreateMode) {
      // In create mode, open the save dialog instead of saving directly
      saveDialog.open();
      reset(); // Cancel the navigation - user will navigate after creating segment
    } else if (segmentId) {
      // Check if user has edit permission
      if (!canEdit) {
        // User doesn't have permission to update - offer "save as new" instead
        saveDialog.open();
        reset(); // Cancel the navigation - user will navigate after creating segment
        return;
      }

      // A shared segment's link follows its saved filters: hand the choice to
      // the shared-segment dialog instead of saving silently.
      if (canManageShare) {
        try {
          const current = await getShareLink(segmentId);
          setShareLink(current);
          if (isShareLinkActive(current)) {
            reset();
            sharedSegmentDialog.open();
            return;
          }
        } catch {
          showError(
            "Couldn't check whether this segment is shared. Try again."
          );
          return;
        }
      }

      // In edit mode with permission, save directly then proceed
      isSavingRef.current = true;
      setIsSaving(true);
      try {
        await updateSegment(segmentId, {
          metadata: {
            filters: withScoringSourceIds(
              serializeAppliedToJson(),
              activeSourceIds
            ),
          },
        });
        markAsPersisted();
        setPersistedSourceIds(activeSourceIds);
        proceed();
      } catch {
        showError("Failed to save segment");
      } finally {
        isSavingRef.current = false;
        setIsSaving(false);
      }
    }
  }, [
    isCreateMode,
    segmentId,
    canEdit,
    canManageShare,
    setShareLink,
    sharedSegmentDialog,
    saveDialog,
    reset,
    proceed,
    serializeAppliedToJson,
    activeSourceIds,
    markAsPersisted,
    showError,
  ]);

  const handleBlockerDiscard = useCallback(() => {
    proceed();
  }, [proceed]);

  const handleBlockerClose = useCallback(() => {
    reset();
  }, [reset]);

  // The segment is saveable whenever there is something to persist: applied
  // filters, or a scoring-profile change (RGI-2264). Dirtiness deliberately does
  // NOT gate this — an opened segment hydrates clean (persistedFilters ===
  // appliedFilters), which used to leave the button disabled while the toolbar
  // showed active filters and results (RGI-2461).
  const isSaveButtonDisabled = !appliedFilters && !scoringChanged;
  const isMissingExportFilters = exportRequiresFilters && !appliedFilters;
  const isExportDisabled = isMissingExportFilters || !canExport;
  const exportTooltipTitle = !canExport
    ? exportDisabledMessage
    : isMissingExportFilters
      ? "Apply filters to enable export"
      : "";

  // Share only ever targets a saved segment's own Market Sizing tab, so it
  // needs a persisted segment (edit mode) the current user can write to,
  // and is only offered while that tab is the one in view.
  const canShowShare = canManageShare && activeTab === MARKET_SIZING_TAB;
  // Someone without write access cannot read or change the link, but the
  // button still shows so they learn why and can share a copy instead.
  const isNotOwner =
    marketSizingTabEnabled &&
    isEditMode &&
    Boolean(segment) &&
    !segment?.permissions?.write;
  const canShowShareAsCopy = isNotOwner && activeTab === MARKET_SIZING_TAB;
  // Freezes config at creation time, so it needs a clean, searchless save —
  // marketSizingReady false is a distinct cause and gets its own message below.
  const shareDisabled =
    !marketSizingReady ||
    hasUnsavedChanges ||
    scoringChanged ||
    urlSearch.trim().length > 0;
  // The saved segment is clean but its Market Sizing settings differ from what
  // the link froze: the Share button itself turns into the "update" action.
  const shareOutOfSync = useMemo(
    () =>
      Boolean(
        shareLink &&
        !shareDisabled &&
        liveShareConfig &&
        hasShareConfigDrifted(
          shareLink.config,
          toShareConfigBody(liveShareConfig, localizeInstalls, scoringV2Enabled)
        )
      ),
    [
      shareLink,
      shareDisabled,
      liveShareConfig,
      localizeInstalls,
      scoringV2Enabled,
    ]
  );
  const shareTooltipTitle = !marketSizingReady
    ? "Open the Market Sizing tab first"
    : shareDisabled
      ? shareLink
        ? "Save your changes (and clear the search), then update the shared link"
        : "Save your changes (and clear the search) to share"
      : shareOutOfSync
        ? "Market Sizing settings changed. Update the shared link to apply them."
        : "";

  const handleShareNotOwnerSaveAsNew = useCallback(() => {
    shareNotOwnerDialog.close();
    saveDialog.open();
  }, [shareNotOwnerDialog, saveDialog]);

  const handleShareClick = useCallback(() => {
    const config = marketSizingRef.current?.getShareConfig();
    if (!config) return;
    shareDialog.open(
      toShareConfigBody(config, localizeInstalls, scoringV2Enabled)
    );
  }, [shareDialog, localizeInstalls, scoringV2Enabled]);

  // Market Sizing settings live in this browser's localStorage; on a fresh
  // browser they start empty, so the link's frozen config seeds them.
  const seedShareConfig = useMemo<MarketSizingShareConfig | undefined>(
    () =>
      shareLink
        ? {
            spendCategoriesByModel: shareLink.config.spend_categories_by_model,
            tierSettingsByModel: shareLink.config.tier_settings_by_model,
            corporateParentOnly: shareLink.config.corporate_parent_only,
          }
        : undefined,
    [shareLink]
  );

  const ActiveFilterComponent =
    activeFilter && isModuleKey(activeFilter)
      ? modules[activeFilter].Component
      : null;

  // The model-score checklist mirrors the active scoring pills: on modal open,
  // seed the draft with a selection-only entry per active model (keeping any
  // criteria entries, including ones whose model was deactivated — reconfirming
  // reactivates them) and drop stale selection-only entries for models no
  // longer active. Selection-only entries never compose into filters, so this
  // doesn't register as a pending filter change.
  const seedModelScoreSelection = useCallback(() => {
    if (!scoringV2Enabled) return;
    const withCriteria = slices.modelScore.entries.filter(
      modelScoreEntryHasCriteria
    );
    const covered = new Set(withCriteria.map((e) => e.modelId));
    const seeded = [
      ...withCriteria,
      ...activeModelFamilies
        .filter((f) => !covered.has(f))
        .map((f) => ({ modelId: f })),
    ];
    const current = slices.modelScore.entries;
    const sameSelection =
      current.length === seeded.length &&
      seeded.every((e, i) => current[i]?.modelId === e.modelId);
    if (!sameSelection) setSlice("modelScore", { entries: seeded });
  }, [scoringV2Enabled, slices.modelScore, activeModelFamilies, setSlice]);

  // The filters modal edits a single filter at a time, so opening it always
  // lands on a concrete category. Inline chips deep-link to their own filter.
  const openFiltersModal = useCallback(
    (filterKey: FilterKey) => {
      seedModelScoreSelection();
      setActiveFilter(filterKey);
      setFiltersModalOpen(true);
    },
    [seedModelScoreSelection]
  );

  // Apply the draft edits to the live results, then close the modal. The
  // model-score checklist mirrors the active scoring pills (RGI-2348), so
  // confirming makes the active V2 models match the selection: unchecked
  // active models are deactivated, selected inactive ones are activated (a
  // stored filter applies either way — RGI-2347 — but displaying the model
  // shows the user the scores they filtered by). V1 profiles are never
  // touched here.
  const handleConfirmFilters = useCallback(() => {
    if (scoringV2Enabled) {
      const selected = new Set(slices.modelScore.entries.map((e) => e.modelId));
      const deactivated = activeModelFamilies.filter((f) => !selected.has(f));
      deactivated.forEach((f) => deactivateSource(v2SourceId(f)));

      const catalogFamilies = new Set(models.map((m) => m.family));
      const missing = slices.modelScore.entries
        .map((e) => e.modelId)
        .filter(
          (f) => catalogFamilies.has(f) && !activeModelFamilies.includes(f)
        );
      const budget =
        MAX_ACTIVE_PROFILES - (activeSourceIds.length - deactivated.length);
      missing.slice(0, budget).forEach((f) => activateSource(v2SourceId(f)));
      if (missing.length > budget) {
        // The panel's cap guard makes this unreachable except for a race
        // (e.g. a pill added from the toolbar while the modal was open).
        showInfo(
          `Scoring is limited to ${MAX_ACTIVE_PROFILES} active profiles/models — some filtered models could not be activated.`
        );
      }
    }
    applyDraft();
    setFiltersModalOpen(false);
  }, [
    applyDraft,
    scoringV2Enabled,
    models,
    slices.modelScore,
    activeModelFamilies,
    activeSourceIds,
    activateSource,
    deactivateSource,
    showInfo,
  ]);

  // The model-score selection isn't part of the composed filters, so pending
  // detection misses it: a checklist-only edit is dirty when the selection
  // diverges from the active V2 models (order-insensitive).
  const scoringSelectionDirty = useMemo(() => {
    if (!scoringV2Enabled) return false;
    const selected = slices.modelScore.entries.map((e) => e.modelId);
    return !sameSourceSelection(selected, activeModelFamilies);
  }, [scoringV2Enabled, slices.modelScore, activeModelFamilies]);
  const hasUnconfirmedFilterEdits = hasPendingChanges || scoringSelectionDirty;

  // Closing (X, backdrop, Escape, Cancel) guards unconfirmed edits behind a
  // discard prompt; otherwise it just closes.
  const handleRequestCloseFilters = useCallback(() => {
    if (hasUnconfirmedFilterEdits) {
      setDiscardFiltersOpen(true);
      return;
    }
    setFiltersModalOpen(false);
  }, [hasUnconfirmedFilterEdits]);

  const handleDiscardFilters = useCallback(() => {
    revertDraftToApplied();
    setDiscardFiltersOpen(false);
    setFiltersModalOpen(false);
  }, [revertDraftToApplied]);

  const handleKeepEditingFilters = useCallback(() => {
    setDiscardFiltersOpen(false);
  }, []);

  const handleLocalizedInstallsChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setLocalizeInstalls(event.target.checked);
    },
    [setLocalizeInstalls]
  );

  // ── Scoring matrix / topbar derived state ──────────────────────────────────
  const focusedProfile =
    profiles.find((p) => p.id === focusedProfileId) ?? null;
  const compareProfile =
    profiles.find((p) => p.id === compareProfileId) ?? null;
  const canCompare = activeProfileIds.length >= 2 && compareProfile != null;
  // The fit×intent matrix is driven by a V1 scoring profile, so only offer it once
  // one is active. This also hides it for advanced (V2) models — they expose a
  // single blended score, not fit/intent axes (revisit once models expose more
  // dimensions). When unavailable, force the list even if "matrix" was persisted.
  const matrixAvailable = scoringEnabled && activeProfileIds.length > 0;
  const effectiveViewMode: ExploreViewMode = matrixAvailable
    ? viewMode
    : "list";
  const isMatrix = scoringEnabled && effectiveViewMode === "matrix";
  const showCompareMatrix = isMatrix && compareMode && canCompare;
  const focusedColor = profileColor(
    focusedProfileId != null ? activeProfileIds.indexOf(focusedProfileId) : 0
  );
  const compareColor = profileColor(
    compareProfileId != null ? activeProfileIds.indexOf(compareProfileId) : 1
  );

  const refreshedLabel = formatRefreshed(segment?.updated_at);

  // Convert the focused band into ephemeral fit/intent score ranges for the List.
  const listDrillBand = useMemo(() => {
    if (!drillBand) return null;
    return {
      fit: bandRange(drillBand.fit, thresholds),
      intent: bandRange(drillBand.intent, thresholds),
    };
  }, [drillBand, thresholds]);

  // While a matrix cell focus is active it controls fit/intent, so lock those
  // filters in the drawer to avoid a conflicting manual override.
  const drawerCategories = useMemo(() => {
    if (!drillBand) return filteredCategories;
    return filteredCategories.map((cat) =>
      cat.id === "fitScore" || cat.id === "intentScore"
        ? {
            ...cat,
            enabled: false,
            disabledReason:
              "Set by the matrix focus — clear the focus chip to edit.",
          }
        : cat
    );
  }, [filteredCategories, drillBand]);

  // "Filters" button and the "+N more" chip open the modal on the first
  // available category (no Overview panel anymore).
  const defaultFilterKey = useMemo<FilterKey>(() => {
    const firstEnabled = drawerCategories.find((c) => c.enabled);
    return (firstEnabled?.id ??
      drawerCategories[0]?.id ??
      "industry") as FilterKey;
  }, [drawerCategories]);

  const handleOpenFiltersDefault = useCallback(() => {
    openFiltersModal(defaultFilterKey);
  }, [openFiltersModal, defaultFilterKey]);

  // "Clear All" on the inline chips bar is a direct page action: clear the
  // draft and apply it immediately so the results update without a confirm
  // step. persistedFilters is left intact so edit-mode dirty detection still
  // flags the segment as having unsaved changes.
  const handleClearAllFilters = useCallback(() => {
    clearDraft();
    applyDraft();
  }, [clearDraft, applyDraft]);

  // Remove a single applied filter from the inline filters popover. Mutates the
  // draft (via the chip's own remove) then applies immediately, mirroring
  // handleClearAllFilters so persistedFilters stays intact.
  const handleRemoveChip = useCallback(
    (chip: FilterChip) => {
      chip.onRemove();
      applyDraft();
    },
    [applyDraft]
  );

  // Matrix view doesn't mount the List (which normally reports the count), so
  // fetch the total here to feed the matrix. Skipped in List view.
  const matrixActiveProfiles = useMemo(
    () =>
      activeProfileIds
        .map((id) => profiles.find((p) => p.id === id))
        .filter((p): p is ScoringProfile => p != null),
    [activeProfileIds, profiles]
  );
  // Profiles eligible to be the Compare target (everything except the focused A).
  const compareCandidates = useMemo(
    () => matrixActiveProfiles.filter((p) => p.id !== focusedProfileId),
    [matrixActiveProfiles, focusedProfileId]
  );
  const matrixScoringInput = useMemo(
    () => transformFilterScoring(matrixActiveProfiles),
    [matrixActiveProfiles]
  );
  const {
    matrixBaseFilters,
    marketSizingFilters,
    matrixQueryModels,
    matrixModelScoreFilter,
    marketSizingModelScoreFilter,
    matrixModelScores,
  } = useMarketSizingQueryArgs({
    appliedFilters,
    urlSearch,
    scoringV2Enabled,
    activeModelFamilies,
    models,
  });
  // Non-filtering models are LEFT joins on a unique hg_id, so they can't change
  // the count, yet hermes still scans each one's full score Parquet.
  const countModelScores = useMemo(
    () =>
      matrixModelScores.filter((m) =>
        matrixModelScoreFilter?.some((f) => f.modelId === m.modelId)
      ),
    [matrixModelScores, matrixModelScoreFilter]
  );
  const { data: matrixCountData } = useGetCompanyListBasicQuery({
    variables: {
      filters: matrixBaseFilters,
      localizeInstalls,
      page: 0,
      per: 1,
      sort: [{ field: "revenue_range_min", direction: SortDir.DESC }],
      scoringProfiles:
        matrixScoringInput.length > 0 ? matrixScoringInput : undefined,
      modelScoreFilter: matrixModelScoreFilter,
      // modelScores + tenantId resolve the model so modelScoreFilter can apply;
      // without them hermes errors ("model_id must be one of the request's
      // model_scores") and this cap-count silently falls back to unfiltered.
      // The count must match the list query + the create payload.
      tenantId:
        countModelScores.length > 0 ? (getTenantId() ?? undefined) : undefined,
      modelScores: countModelScores.length > 0 ? countModelScores : undefined,
    },
    context: { clientName: COMPANY_SEARCH_CLIENT },
    // Fetch in both views when filters are applied: matrix uses it for the total
    // count, and the save-as-workbook cap gate needs this base count (below).
    skip: !appliedFilters,
  });
  useEffect(() => {
    const c = matrixCountData?.opportunity_generator_search?.count;
    if (isMatrix && c != null) setCompanyCount(c);
  }, [isMatrix, matrixCountData]);

  // Count of the exact set the workbook would save — base filters + scoring, no
  // ephemeral fuzzy-search/drill-band overlays (same inputs as the create
  // payload). Gate the 50k cap on this, not the on-screen `companyCount`, so the
  // UI matches what emback will resolve.
  const overWorkbookLimit =
    (matrixCountData?.opportunity_generator_search?.count ?? 0) >
    MAX_WORKBOOK_COMPANIES;

  return (
    <Stack
      direction="column"
      height="100%"
      minWidth={0}
      overflow="hidden"
      sx={{ px: 3, py: 2 }}
    >
      <Box pl={1} mb={1} minWidth={0}>
        <SegmentBreadcrumb
          segmentId={segmentId}
          segmentName={segment?.name}
          isLoading={isLoadingSegment}
          mode={breadcrumbMode}
          allSegments={allSegments}
        />
      </Box>
      <Box
        pl={1}
        mb={1}
        display="grid"
        gridTemplateColumns="minmax(0, 1fr) auto"
        alignItems="center"
        columnGap={2}
        minWidth={0}
      >
        <Stack direction="row" spacing={1.5} alignItems="baseline" minWidth={0}>
          <Typography
            component="h1"
            fontSize="26px"
            fontWeight={300}
            lineHeight={1.15}
            minWidth={0}
            noWrap
            title={isLoadingSegment ? undefined : pageTitle}
          >
            {isLoadingSegment ? <Skeleton width={200} /> : pageTitle}
          </Typography>
          {!isLoadingSegment && refreshedLabel && (
            <Typography
              variant="body2"
              color="text.secondary"
              noWrap
              sx={{ flexShrink: 0 }}
            >
              {refreshedLabel}
            </Typography>
          )}
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ minWidth: "max-content" }}
        >
          <CreditProgressBar loadingVariant="skeleton" compact />
          <Tooltip
            title={
              !canSaveAsWorkbook
                ? "You don't have AI Sales Copilot access. Contact customer support."
                : !appliedFilters
                  ? "Apply filters first"
                  : overWorkbookLimit
                    ? `Segments over ${MAX_WORKBOOK_COMPANIES.toLocaleString()} companies can't be saved as a list. Refine your filters.`
                    : ""
            }
          >
            <span>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<LibraryBooksIcon />}
                disabled={
                  !canSaveAsWorkbook || !appliedFilters || overWorkbookLimit
                }
                onClick={() => saveAsWorkbookDialog.open()}
                id="segment-save-as-workbook-btn"
                data-testid="segment-save-as-workbook-btn"
              >
                Add to Sales Copilot
              </Button>
            </span>
          </Tooltip>
          {activeTab !== MARKET_SIZING_TAB && (
            <Tooltip title={exportTooltipTitle}>
              <span>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<DownloadIcon />}
                  disabled={isExportDisabled}
                  // Market Sizing export is suppressed at the trigger; ExportChoiceDialog
                  // and handleExportMarketSizing are kept in place for re-enabling later.
                  onClick={() => exportDialog.open()}
                  id="segment-export-btn"
                  data-testid="scoring-export-btn"
                >
                  Export
                </Button>
              </span>
            </Tooltip>
          )}
          {canShowShare && (
            <Tooltip title={shareTooltipTitle}>
              <span>
                <Badge
                  variant="dot"
                  color="warning"
                  overlap="rectangular"
                  invisible={!shareOutOfSync}
                  data-testid="market-sizing-share-out-of-sync"
                >
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<ShareIcon />}
                    disabled={shareDisabled}
                    onClick={handleShareClick}
                    id="market-sizing-share-btn"
                    data-testid="market-sizing-share-btn"
                  >
                    {shareOutOfSync ? "Update Shareable Link" : "Share"}
                  </Button>
                </Badge>
              </span>
            </Tooltip>
          )}
          {canShowShareAsCopy && (
            <Tooltip title="Only the segment owner can share it">
              <span>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<ShareIcon />}
                  onClick={() => shareNotOwnerDialog.open()}
                  id="market-sizing-share-btn"
                  data-testid="market-sizing-share-btn"
                >
                  Share
                </Button>
              </span>
            </Tooltip>
          )}
          <Tooltip
            title={isSaveButtonDisabled ? "Apply filters to enable saving" : ""}
          >
            <span>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                disabled={isSaveButtonDisabled}
                onClick={saveActionHandlers[saveButtonConfig.action]}
                sx={{ whiteSpace: "nowrap" }}
                id="segment-save-btn"
                data-testid="scoring-profile-save-btn"
              >
                {saveButtonConfig.label}
              </Button>
            </span>
          </Tooltip>

          {isEditMode &&
            (menuActions.showSaveAsNew ||
              menuActions.showEdit ||
              menuActions.showDelete) && (
              <>
                <IconButton
                  size="small"
                  onClick={handleMenuOpen}
                  id="segment-more-menu-btn"
                  data-testid="segment-more-menu-btn"
                >
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  anchorEl={menuAnchorEl}
                  open={menuOpen}
                  onClose={handleMenuClose}
                  transformOrigin={{ horizontal: "right", vertical: "top" }}
                  anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                  id="segment-more-menu"
                  slotProps={{
                    paper: {
                      sx: { minWidth: 180 },
                    },
                  }}
                >
                  {menuActions.showSaveAsNew && (
                    <MenuItem
                      onClick={handleSaveAsNewClick}
                      id="segment-save-as-new-item"
                      data-testid="scoring-profile-saveas-btn"
                    >
                      <ListItemIcon>
                        <SaveIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Save as new</ListItemText>
                    </MenuItem>
                  )}
                  {menuActions.showDivider && <Divider />}
                  {menuActions.showEdit && (
                    <MenuItem
                      onClick={handleEditClick}
                      id="segment-edit-details-item"
                      data-testid="scoring-profile-edit-btn"
                    >
                      <ListItemIcon>
                        <EditOutlinedIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Edit Details</ListItemText>
                    </MenuItem>
                  )}
                  {menuActions.showDelete && (
                    <MenuItem
                      onClick={handleDeleteClick}
                      id="segment-delete-item"
                      data-testid="scoring-profile-delete-btn"
                    >
                      <ListItemIcon>
                        <DeleteOutlineIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Delete</ListItemText>
                    </MenuItem>
                  )}
                </Menu>
              </>
            )}
        </Stack>
      </Box>

      <Box ref={scoringPickerRef}>
        <ScoringExploreTopbar
          scoringEnabled={scoringEnabled}
          onCreateNewProfile={() => {
            // Without the chooser, authoring goes straight to the admin AI-scoring
            // tool; fall back to the wizard if that URL can't be built.
            if (!showMethodModal && openAiScoring(getTenantId())) return;
            setWizardOpen(true);
          }}
          onEditProfile={(p) => {
            setEditingProfile(p);
            setProfileDialogOpen(true);
          }}
          viewMode={scoringEnabled ? effectiveViewMode : "list"}
          onViewModeChange={handleViewModeChange}
          // The List/Matrix toggle + Compare button are List-tab concepts —
          // hide them on the Market Sizing tab. Search/filters/scoring stay
          // visible on both tabs since they define the same underlying
          // market either way.
          matrixAvailable={matrixAvailable && activeTab === "list"}
          compareMode={compareMode}
          onToggleCompare={() => setCompareMode((v) => !v)}
          canCompare={canCompare}
          searchSlot={
            <CompaniesExploreSearchInput
              initialValue={urlSearch}
              onDebouncedChange={handleDebouncedSearchChange}
            />
          }
          filtersSlot={
            <SegmentFiltersControl
              chips={enrichedChips}
              onAddFilter={handleOpenFiltersDefault}
              onClearAll={handleClearAllFilters}
              onRemoveChip={handleRemoveChip}
              onChipClick={(chip) => openFiltersModal(chip.filterKey)}
            />
          }
        />
      </Box>

      {marketSizingTabEnabled && (
        <Box pl={1}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              minHeight: 36,
              "& .MuiTab-root": {
                textTransform: "none",
                minWidth: 0,
                minHeight: 36,
                px: 0,
                mr: 3,
                fontWeight: 500,
              },
            }}
          >
            <Tab
              value="list"
              label="Company List"
              data-testid="companies-list-tab"
            />
            <Tab
              value={MARKET_SIZING_TAB}
              label="Market Sizing"
              data-testid="market-sizing-tab"
            />
          </Tabs>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          mt: 1,
          overflow: "hidden",
        }}
      >
        {marketSizingVisited && (
          // Mounted (hidden) once visited rather than unmounted on every tab
          // switch away — ScoringProfileTabs keeps each profile's tier
          // selection in local state, seeded from the shared filter only at
          // mount, so tearing it down on tab switches loses that memory.
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: activeTab === MARKET_SIZING_TAB ? "1 1 auto" : "0 0 0px",
              minHeight: 0,
              overflow: "hidden",
              visibility:
                activeTab === MARKET_SIZING_TAB ? "visible" : "hidden",
            }}
          >
            <Suspense
              fallback={
                <Skeleton variant="rounded" height={400} sx={{ mx: 1 }} />
              }
            >
              <MarketSizingContent
                // Forces a full remount on segment switches so persisted spend
                // categories (and any other internal state) never leak from one
                // segment's Market Sizing tab into another's.
                key={segmentId ?? "explore"}
                ref={setMarketSizingRef}
                onShareConfigChange={setLiveShareConfig}
                seedShareConfig={seedShareConfig}
                segmentId={segmentId}
                isActive={activeTab === MARKET_SIZING_TAB}
                filters={marketSizingFilters}
                filterCount={enrichedChips.length + (urlSearch.trim() ? 1 : 0)}
                filterLabels={marketSizingFilterLabels}
                localizeInstalls={localizeInstalls}
                modelScoreFilter={marketSizingModelScoreFilter}
                modelScores={matrixModelScores}
                tenantId={
                  matrixModelScores.length > 0
                    ? (getTenantId() ?? undefined)
                    : undefined
                }
                onOpenFilters={handleOpenFiltersDefault}
                onOpenScoring={handleOpenScoringPicker}
              />
            </Suspense>
          </Box>
        )}
        {/* Kept mounted (hidden) on the Market Sizing tab so the list's
            pagination/sort state survives tab switches. Collapsed via
            flex-basis + visibility rather than display:none/contents — the
            DataGrid's ResizeObserver needs a real flex box in the tree at
            all times, or it logs a false "empty height" warning when the
            box reappears. */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: activeTab === MARKET_SIZING_TAB ? "0 0 0px" : "1 1 auto",
            minHeight: 0,
            overflow: "hidden",
            visibility: activeTab === MARKET_SIZING_TAB ? "hidden" : "visible",
          }}
        >
          {showCompareMatrix ? (
            <ScoringCompareMatrix
              profileA={focusedProfile}
              profileB={compareProfile}
              colorA={focusedColor}
              colorB={compareColor}
              compareCandidates={compareCandidates}
              onCompareProfileChange={setCompareProfileId}
              baseFilters={matrixBaseFilters}
              localizeInstalls={localizeInstalls}
              thresholds={thresholds}
              onThresholdsChange={setThresholds}
            />
          ) : isMatrix ? (
            <ScoringMatrix
              profile={focusedProfile}
              dotColor={focusedColor}
              baseFilters={matrixBaseFilters}
              localizeInstalls={localizeInstalls}
              thresholds={thresholds}
              onThresholdsChange={setThresholds}
              onCellClick={handleMatrixCellClick}
            />
          ) : (
            <>
              {drillBand && (
                <Box pl={1} mb={1}>
                  <Chip
                    color="primary"
                    variant="outlined"
                    onDelete={() => setDrillBand(null)}
                    data-testid="drill-band-chip"
                    label={`Focused: ${BAND_LABEL[drillBand.fit]} fit · ${BAND_LABEL[drillBand.intent]} intent`}
                  />
                </Box>
              )}
              <CompaniesExploreList
                filters={appliedFilters}
                fuzzySearch={urlSearch}
                localizeInstalls={localizeInstalls}
                drillBand={listDrillBand}
                onCountChange={(count) => setCompanyCount(count ?? 0)}
                onEditProfile={(p) => {
                  setEditingProfile(p);
                  setProfileDialogOpen(true);
                }}
                onActiveSortChange={handleActiveSortChange}
              />
            </>
          )}
        </Box>
      </Box>

      <SegmentDialog
        open={saveDialog.isOpen}
        mode="create"
        title={isEditMode ? "Save as New Segment" : undefined}
        initialName={
          isEditMode && segment?.name
            ? `${segment.name} - Clone`.slice(0, 200)
            : undefined
        }
        onClose={saveDialog.close}
        onSave={handleSaveComplete}
        filtersJson={withScoringSourceIds(
          serializeAppliedToJson(),
          activeSourceIds
        )}
      />

      <SaveAsWorkbookDialog
        open={saveAsWorkbookDialog.isOpen}
        onClose={saveAsWorkbookDialog.close}
        initialName={segment?.name}
        companyCount={
          matrixCountData?.opportunity_generator_search?.count ?? undefined
        }
        models={matrixQueryModels}
        canLinkOffering={canLinkOffering}
        payload={{
          filters: matrixBaseFilters ?? {},
          scoringProfiles:
            matrixScoringInput.length > 0 ? matrixScoringInput : undefined,
          localizeInstalls,
          // The active V2 models always go, so the list keeps them for Sales
          // Copilot; the filter (+ the tenant it needs) only when one is
          // active. Emback exports with just the models a filter targets.
          ...(matrixModelScores.length > 0
            ? { modelScores: matrixModelScores }
            : {}),
          ...(matrixModelScoreFilter
            ? {
                modelScoreFilter: matrixModelScoreFilter,
                tenantId: getTenantId() ?? undefined,
              }
            : {}),
        }}
      />

      <ShareLinkDialog
        open={shareDialog.isOpen}
        onClose={shareDialog.close}
        segmentId={segmentId}
        config={shareDialog.data}
        existingLink={shareLink}
        onLinkChange={setShareLink}
      />

      <ShareNotOwnerDialog
        open={shareNotOwnerDialog.isOpen}
        onClose={shareNotOwnerDialog.close}
        onSaveAsNew={handleShareNotOwnerSaveAsNew}
      />

      <SaveSharedSegmentDialog
        open={sharedSegmentDialog.isOpen}
        isSaving={isSavingShared}
        onClose={sharedSegmentDialog.close}
        onSaveAndUpdate={handleSaveAndUpdateSharedLink}
        onSaveAsNew={handleSaveSharedAsNew}
      />

      <SegmentModificationDialogs
        deleteDialog={deleteDialog}
        editDialog={editDialog}
        onDelete={handleConfirmDelete}
        onEditSave={handleEditComplete}
        isDeleting={isDeleting}
        segmentName={segment?.name}
      />

      <CompanyExportModal
        open={exportDialog.isOpen}
        onClose={exportDialog.close}
        filters={
          appliedFilters && { ...appliedFilters, fuzzySearch: urlSearch.trim() }
        }
        companyCount={companyCount}
        localizeInstalls={localizeInstalls}
        activeSortEntry={activeSortEntry}
        allowSaveQuote
        scoringSourceIds={activeSourceIds}
        quote={quote}
        initialSettings={quote?.export_settings}
        onQuoteSaved={handleQuoteSaved}
      />

      {marketSizingTabEnabled && (
        <ExportChoiceDialog
          open={exportChoiceDialog.isOpen}
          onClose={exportChoiceDialog.close}
          onSelectCompanyList={() => {
            exportChoiceDialog.close();
            exportDialog.open();
          }}
          onSelectMarketSizing={handleExportMarketSizing}
          marketSizingAvailable={!!appliedFilters}
          exportingMarketSizing={marketSizingExporting}
          onCancelExport={() => {
            exportCancelledRef.current = true;
          }}
        />
      )}

      <UnsavedSegmentChangesDialog
        open={isBlocked}
        onClose={handleBlockerClose}
        onDiscard={handleBlockerDiscard}
        onSave={handleBlockerSave}
        isSaving={isSaving}
        isCreateMode={isCreateMode}
      />

      {scoringEnabled && (
        <Suspense fallback={null}>
          <ScoringProfileDialog
            open={profileDialogOpen}
            profile={editingProfile}
            initialCriteria={generatedCriteria}
            initialName={generatedName}
            onClose={() => {
              setProfileDialogOpen(false);
              setGeneratedCriteria(undefined);
              setGeneratedName(undefined);
            }}
            onSaved={() => {
              setProfileDialogOpen(false);
              setGeneratedCriteria(undefined);
              setGeneratedName(undefined);
            }}
          />
        </Suspense>
      )}
      {scoringEnabled && (
        <Suspense fallback={null}>
          <ScoringWizardDialog
            open={wizardOpen}
            onClose={() => setWizardOpen(false)}
            segments={allSegments}
            onCreateWithAi={() => openAiScoring(getTenantId())}
            onBuildManually={() => {
              setWizardOpen(false);
              setEditingProfile(null);
              setProfileDialogOpen(true);
            }}
            onGenerated={(criteria, name) => {
              setWizardOpen(false);
              setEditingProfile(null);
              setGeneratedCriteria(criteria);
              setGeneratedName(name);
              setProfileDialogOpen(true);
            }}
          />
        </Suspense>
      )}

      <FiltersModal
        open={filtersModalOpen}
        onClose={handleRequestCloseFilters}
        footer={
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1.5 }}
          >
            <Typography variant="body2" color="text.secondary">
              {hasUnconfirmedFilterEdits
                ? "You have unconfirmed filter changes"
                : "Filters are up to date"}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                color="secondary"
                onClick={handleRequestCloseFilters}
                data-testid="filters-modal-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmFilters}
                disabled={!hasUnconfirmedFilterEdits}
                data-testid="filters-modal-confirm-btn"
              >
                Confirm filters
              </Button>
            </Stack>
          </Stack>
        }
      >
        <FiltersSidebar
          activeFilter={activeFilter}
          onFilterSelect={setActiveFilter}
          getFilterCount={getFilterCount}
          categories={drawerCategories}
          showKnownCompanies={!!superKudu}
          showSuperKuduFilters={!!superKudu}
          footer={
            <LocalizedInstallsFooter
              checked={localizeInstalls}
              onChange={handleLocalizedInstallsChange}
            />
          }
        />
        <FilterPanel
          title={activeFilter ? FILTER_TITLES[activeFilter] : "Add a filter"}
          onClose={handleRequestCloseFilters}
          fill
        >
          {activeFilter && ActiveFilterComponent ? (
            <ActiveFilterComponent />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {activeFilter
                ? "This filter is not yet implemented."
                : "Pick a category on the left to add or edit a filter."}
            </Typography>
          )}
        </FilterPanel>
      </FiltersModal>

      <ConfirmationDialog
        open={discardFiltersOpen}
        title="Discard filter changes?"
        description="You have filter changes that haven't been confirmed. Discard them and close?"
        okayButtonText="Discard changes"
        cancelButtonText="Keep editing"
        onConfirm={handleDiscardFilters}
        onClose={handleKeepEditingFilters}
      />
    </Stack>
  );
};

export const CompaniesExplorePage: FC = () => {
  const [searchCompanies] = useLazyQuery<SearchCompaniesV2Response>(
    SEARCH_COMPANY_QUERY,
    { context: { clientName: COMPANY_SEARCH_CLIENT } }
  );
  const [fetchCompaniesByIds] = useLazyQuery<CompanySearchResponse>(
    COMPANY_SEARCH_SLIM_QUERY,
    { context: { clientName: COMPANY_SEARCH_CLIENT } }
  );
  const [uploadFile] = useUploadLazyQuery({
    context: { clientName: COMPANY_MATCH_CLIENT },
    fetchPolicy: "no-cache",
  });
  const [fetchStats] = useStatsLazyQuery({
    context: { clientName: COMPANY_MATCH_CLIENT },
    fetchPolicy: "no-cache",
  });
  const [uploadKnownCompanies] = useUploadKnownCompaniesMutation({
    context: { clientName: COMPANY_SEARCH_UPLOAD_CLIENT },
  });
  const [fetchKnownCompaniesUpload] = useKnownCompaniesUploadLazyQuery({
    context: { clientName: COMPANY_SEARCH_CLIENT },
    fetchPolicy: "no-cache",
  });

  const {
    countries,
    regions,
    states,
    spendCategories,
    hgIndustries,
    sic,
    naics,
    products,
    vendors,
    productCategories,
    productAttributes,
  } = useTaxonomyContext();

  // Intent filter metadata queries
  const { data: journeysData } = useGetIntentBuyersJourneyQuery({
    context: { clientName: TAXONOMY_CLIENT },
  });
  const { data: contextsData } = useGetIntentContextTypesQuery({
    context: { clientName: TAXONOMY_CLIENT },
  });

  const geographyOptions = useMemo<GeographyOptions>(
    () => toGeographyOptions(countries, regions),
    [countries, regions]
  );

  const itSpendOptions = useMemo<ItSpendOptions>(
    () => ({
      spendCategories,
    }),
    [spendCategories]
  );

  const aiSpendCategories = useAiSpendCategories();
  const aiSpendOptions = useMemo<AiSpendOptions>(
    () => ({ aiSpendCategories }),
    [aiSpendCategories]
  );

  const intentOptions = useMemo<IntentOptions>(
    () => ({
      buyersJourneyOptions: (journeysData?.intentBuyersJourney?.data ?? [])
        .filter((j): j is NonNullable<typeof j> => j !== null && j.id !== null)
        .map((j) => ({
          id: j.id as string,
          name: j.name ?? "Unknown",
          description: j.description ?? undefined,
        })),
      contextTypeOptions: (contextsData?.intentContextType?.data ?? [])
        .filter((c): c is NonNullable<typeof c> => c !== null && c.id !== null)
        .map((c) => ({
          id: c.id as string,
          name: c.name ?? "Unknown",
          description: c.description ?? undefined,
        })),
    }),
    [journeysData, contextsData]
  );

  // V2 scoring models resolve model-score chip names; the lookup stays absent
  // (chips show "Loading...") until the argo catalog has loaded.
  const scoringModels = useScoringStore((s) => s.models);

  const filterLookups = useMemo<FilterLookups>(
    () =>
      buildCompanyFilterLookups({
        countries,
        states,
        hgIndustries,
        sic,
        naics,
        products,
        vendors,
        productCategories,
        productAttributes,
        spendCategories,
        aiSpendCategories,
        buyersJourneys: intentOptions.buyersJourneyOptions,
        contextTypes: intentOptions.contextTypeOptions,
        scoringModels: scoringModels.length > 0 ? scoringModels : undefined,
      }),
    [
      hgIndustries,
      sic,
      naics,
      products,
      vendors,
      productCategories,
      productAttributes,
      spendCategories,
      aiSpendCategories,
      countries,
      states,
      intentOptions.buyersJourneyOptions,
      intentOptions.contextTypeOptions,
      scoringModels,
    ]
  );

  const targetOrganizationsOptions = useMemo<TargetOrganizationsOptions>(
    () => ({
      onSearch: async (query: string) => {
        const result = await searchCompanies({
          variables: {
            query,
            per: 15,
            sort: [{ field: "REVENUE_RANGE_MIN", direction: "DESC" as const }],
          },
        });
        const companies = result.data?.searchCompaniesV2?.data ?? [];
        return companies.map(spogCompanyToCached);
      },
      onHydrate: async (ids: string[]) => {
        if (ids.length === 0) return [];
        const result = await fetchCompaniesByIds({
          variables: { filters: { hgIds: ids }, per: ids.length },
        });
        const companies = result.data?.search?.data ?? [];
        return companies.map(spogCompanyToCached);
      },
    }),
    [fetchCompaniesByIds, searchCompanies]
  );

  const uploadedAccountsOptions = useMemo<UploadedAccountsOptions>(
    () => ({
      onUpload: async (
        file: File,
        _includeHierarchy: boolean,
        enforceCountryMatch: boolean,
        companyConfig
      ) => {
        const { isPlatformPermissible, isV1Dataset } = deriveTierFlags(
          companyConfig?.tierSetting ?? "isPlatformPermissible"
        );
        const result = await uploadFile({
          variables: {
            file,
            isPlatformPermissible,
            isV1Dataset,
            rgifExclusiveCompany: companyConfig?.rgifExclusiveCompany ?? true,
            enforceCountryMatch,
          },
        });

        // Check for GraphQL errors first
        if (result.errors?.length) {
          const errorMessage = result.errors.map((e) => e.message).join("; ");
          throw new Error(errorMessage || "Upload failed");
        }

        if (!result.data?.upload?.fileId) {
          throw new Error("Upload failed - no file ID returned");
        }
        return {
          fileId: result.data.upload.fileId,
          fileName: result.data.upload.fileName ?? file.name,
          totalRows: result.data.upload.totalRows ?? null,
        };
      },
      onPoll: async (fileId: string) => {
        const result = await fetchStats({
          variables: { fileId },
        });
        if (!result.data?.stats) {
          throw new Error("Failed to fetch stats");
        }
        return {
          fileId: result.data.stats.fileId ?? fileId,
          fileName: result.data.stats.fileName ?? null,
          totalRows: result.data.stats.totalRows ?? null,
          matchedRows: result.data.stats.matchedRows ?? null,
          invalidRows: result.data.stats.invalidRows ?? null,
          uploaded: result.data.stats.uploaded ?? null,
          lastMatched: result.data.stats.lastMatched ?? null,
          isPlatformPermissible:
            result.data.stats.isPlatformPermissible ?? null,
          isV1Dataset: result.data.stats.isV1Dataset ?? null,
          rgifExclusiveCompany: result.data.stats.rgifExclusiveCompany ?? null,
        };
      },
      onDownloadDetails: downloadMatchDetails,
    }),
    [uploadFile, fetchStats]
  );

  const knownCompaniesOptions = useMemo<KnownCompaniesOptions>(
    () => ({
      onUpload: async (file, column) => {
        const result = await uploadKnownCompanies({
          variables: { file, ...column },
        });
        if (result.errors?.length) {
          throw new Error(result.errors.map((e) => e.message).join("; "));
        }
        const identifier = result.data?.uploadKnownCompanies?.identifier;
        if (!identifier) {
          throw new Error("Upload failed - no identifier returned");
        }
        return { identifier };
      },
      onPoll: async (identifier) => {
        const result = await fetchKnownCompaniesUpload({
          variables: { identifier },
        });
        const upload = result.data?.knownCompaniesUpload;
        if (!upload) {
          throw new Error("Failed to fetch the upload status");
        }
        if (upload.status === KnownCompaniesUploadStatus.ERROR) {
          throw new Error(upload.error || "Processing failed");
        }
        return {
          fileId: identifier,
          fileName: null,
          totalRows: upload.totalRows,
          matchedRows: upload.matchedRows,
          invalidRows: upload.invalidRows,
          uploaded: null,
          // useUploadPolling stops once this is set
          lastMatched:
            upload.status === KnownCompaniesUploadStatus.SUCCESS
              ? new Date().toISOString()
              : null,
        };
      },
    }),
    [uploadKnownCompanies, fetchKnownCompaniesUpload]
  );

  return (
    <CompanyFilterProvider
      options={{
        geography: geographyOptions,
        itSpend: itSpendOptions,
        aiSpend: aiSpendOptions,
        targetOrganizations: targetOrganizationsOptions,
        uploadedAccounts: uploadedAccountsOptions,
        knownCompanies: knownCompaniesOptions,
        intent: intentOptions,
      }}
      lookups={filterLookups}
    >
      <CompaniesExplorePageContent />
    </CompanyFilterProvider>
  );
};
